import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

const FLOW_RETURN_STORAGE_KEY = "navigatebackfromcreateflow";
const BUNDLE_RETURN_STORAGE_KEY = "navigatebackfromcreatebundle";

@Injectable({
  providedIn: "root",
})
export class FlowNavigationService {
  constructor(private router: Router) {}

  // ── Bundle navigation ──────────────────────────────────────────────────────

  /** Navigate back after closing create/edit bundle. */
  navigateBackFromBundle(rowKey?: string | null): void {
    const stored = localStorage.getItem(BUNDLE_RETURN_STORAGE_KEY) || "";
    localStorage.removeItem(BUNDLE_RETURN_STORAGE_KEY);

    const isSafe =
      stored.length > 0 &&
      !stored.includes("/create-guide-pack") &&
      !stored.includes("/createbundle") &&
      !stored.includes("/edit-guide-pack") &&
      !stored.includes("/editbundle");

    const destination = isSafe
      ? stored
      : rowKey
      ? "/view-guide-pack/" + rowKey
      : "/user-profile";

    console.log("[Bundle leave] stored:", stored, "| destination:", destination);
    this.router.navigateByUrl(destination);
  }

  // ── Flow navigation ────────────────────────────────────────────────────────

  /** Remember the page the user was on before opening a create/edit flow modal. */
  captureReturnPath(path?: string): void {
    const candidate = (path ?? this.router.url).split("#")[0];
    if (!candidate || this.isCreateFlowPath(candidate)) {
      return;
    }
    localStorage.setItem(FLOW_RETURN_STORAGE_KEY, candidate);
  }

  /** Resolve the best return path from query param, storage, or home. */
  getReturnPath(queryReturnUrl?: string | null): string {
    const fromQuery = this.normalizePath(queryReturnUrl);
    if (fromQuery) {
      return fromQuery;
    }

    const stored = this.normalizePath(
      localStorage.getItem(FLOW_RETURN_STORAGE_KEY)
    );
    if (stored) {
      return stored;
    }

    return "/";
  }

  /** Navigate back to the page that opened the create/edit flow flow. */
  navigateBack(queryReturnUrl?: string | null, clearStoredPath = true): void {
    const path = this.getReturnPath(queryReturnUrl);
    if (clearStoredPath) {
      localStorage.removeItem(FLOW_RETURN_STORAGE_KEY);
    }
    this.router.navigateByUrl(path);
  }

  /** Build the public guide URL for a published flow. */
  buildFlowGuidePath(title: string, rowKey: number | string): string {
    const slug = this.slugifyFlowTitle(title);
    return `/guide/${slug}/${rowKey}`;
  }

  /** Navigate to the published flow details page. */
  navigateToPublishedFlow(title: string, rowKey: number | string): void {
    localStorage.removeItem(FLOW_RETURN_STORAGE_KEY);
    this.router.navigateByUrl(this.buildFlowGuidePath(title, rowKey));
  }

  slugifyFlowTitle(title: string): string {
    if (!title) {
      return "flow";
    }

    return title
      .replace(/<[^>]*>/g, "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .toLowerCase() || "flow";
  }

  private normalizePath(path?: string | null): string | null {
    if (!path || typeof path !== "string") {
      return null;
    }

    const trimmed = path.trim();
    if (!trimmed || this.isCreateFlowPath(trimmed)) {
      return null;
    }

    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  }

  private isCreateFlowPath(path: string): boolean {
    const pathOnly = path.split("?")[0].split("#")[0];
    return (
      pathOnly === "/create-guide" ||
      pathOnly.startsWith("/create-guide/") ||
      pathOnly === "/create-flow" ||
      pathOnly.startsWith("/create-flow/")
    );
  }
}
